<template>
  <MainLayout>
    <div 
      :class="theme === 'dark' ? 'tw-bg-[#151515] tw-text-gray-100' : 'tw-bg-gray-100 tw-text-gray-800'" 
      class="tw-min-h-screen tw-p-4 md:tw-p-8"
      :data-theme="theme"
    >
      <div class="tw-mb-6 md:tw-mb-8">
        <h1 :class="theme === 'dark' ? 'tw-text-gray-100' : 'tw-text-gray-800'" class="tw-text-xl md:tw-text-2xl tw-font-bold">
          Relatório de Termometria - {{ unidade.nome || 'Unidade' }}
        </h1>
      </div>

      <div class="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-gap-8">
        <div 
          :class="theme === 'dark' ? 'tw-bg-[#1a1d21] tw-border-gray-800' : 'tw-bg-white tw-border-gray-100'" 
          class="tw-rounded-2xl tw-p-5 md:tw-p-8 tw-shadow-2xl tw-border"
        >
          <div class="tw-flex tw-items-center tw-gap-4 tw-mb-6 md:tw-mb-8">
            <div :class="theme === 'dark' ? 'tw-bg-blue-500/10' : 'tw-bg-blue-50'" class="tw-p-3 tw-rounded-xl">
              <i class="fa-solid fa-filter tw-text-blue-500 tw-text-lg"></i>
            </div>
            <h2 :class="theme === 'dark' ? 'tw-text-gray-100' : 'tw-text-gray-800'" class="tw-text-base md:tw-text-lg tw-font-bold m-0">Filtros de Busca</h2>
          </div>

          <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4 md:tw-gap-6">
            <div class="tw-space-y-2 tw-relative">
              <label class="tw-block tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase">Locais</label>
              <button
                type="button"
                @click="siloDropdownOpen = !siloDropdownOpen"
                class="tw-custom-input tw-border-none tw-flex tw-items-center tw-justify-between tw-text-left tw-cursor-pointer"
              >
                <span class="tw-truncate">{{ siloSelectionLabel }}</span>
                <i class="fa-solid fa-chevron-down tw-text-xs tw-opacity-50 tw-ml-2 tw-shrink-0"></i>
              </button>

              <div v-if="siloDropdownOpen" @click="siloDropdownOpen = false" class="tw-fixed tw-inset-0 tw-z-30"></div>
              <div
                v-if="siloDropdownOpen"
                :class="theme === 'dark' ? 'tw-bg-[#1a1d21] tw-border-gray-700' : 'tw-bg-white tw-border-gray-200'"
                class="tw-absolute tw-z-40 tw-mt-1 tw-w-full tw-max-h-64 tw-overflow-y-auto tw-rounded-xl tw-border tw-shadow-2xl tw-p-2"
              >
                <label class="tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-2 tw-rounded-lg hover:tw-bg-black/5 tw-cursor-pointer tw-border-b tw-border-gray-800/10 tw-mb-1">
                  <input type="checkbox" :checked="todosSilosSelecionados" @change="toggleTodosSilos" class="tw-accent-blue-600" />
                  <span class="tw-text-xs tw-font-bold">Selecionar todos</span>
                </label>
                <label
                  v-for="silo in silosFiltered" :key="silo.id"
                  class="tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-2 tw-rounded-lg hover:tw-bg-black/5 tw-cursor-pointer"
                >
                  <input type="checkbox" :value="silo.id" v-model="selectedSilos" class="tw-accent-blue-600" />
                  <span class="tw-text-xs tw-truncate">{{ silo.nome }}</span>
                </label>
                <p v-if="silosFiltered.length === 0" class="tw-text-xs tw-opacity-50 tw-px-2 tw-py-2">Nenhum silo disponível.</p>
              </div>
            </div>
            <div class="tw-space-y-2">
              <label class="tw-block tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase">Data Início</label>
              <input type="date" v-model="dataInicio" class="tw-custom-input tw-border-none" />
            </div>
            <div class="tw-space-y-2">
              <label class="tw-block tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase">Data Final</label>
              <input type="date" v-model="dataFinal" class="tw-custom-input tw-border-none" />
            </div>
            <div class="tw-flex tw-items-end">
              <button 
                @click="fetchLeituras"
                :disabled="buscandoLeituras"
                class="tw-w-full tw-py-3 md:tw-py-4 tw-rounded-xl tw-bg-blue-600 tw-text-white tw-text-xs tw-font-bold tw-uppercase hover:tw-bg-blue-700 tw-shadow-lg tw-shadow-blue-600/20 tw-border-none disabled:tw-opacity-50"
              >
                {{ buscandoLeituras ? 'Buscando...' : 'Buscar Leituras' }}
              </button>
            </div>
          </div>

          <div>
            <div v-if="leiturasEncontradas.length > 0" class="tw-mt-8 tw-pt-8 tw-border-t tw-border-gray-800/10">
              <div class="tw-mb-6">
                <span class="tw-text-xs tw-font-bold tw-opacity-50">{{ leiturasEncontradas.length }} leitura(s) encontrada(s)</span>
              </div>
              <div class="tw-flex tw-flex-col md:tw-flex-row md:tw-items-end tw-gap-6">
                <div class="tw-space-y-2 md:tw-w-56 md:tw-shrink-0">
                  <label class="tw-block tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase">Horário da Leitura</label>
                  <BaseSelect
                    v-model="selectedLeitura"
                    :options="leiturasAgrupadas"
                    grouped
                    group-label="data"
                    group-options="itens"
                    placeholder="Selecione o horário"
                  >
                    <template #option="{ option }">{{ horaLabel(option) }}</template>
                    <template #value="{ selected }">{{ selected ? selected.data : '' }}</template>
                  </BaseSelect>
                </div>
                <div class="tw-flex tw-items-center tw-gap-3 tw-pb-3 md:tw-shrink-0">
                   <label for="colorido" class="tw-flex tw-items-center tw-gap-3 tw-cursor-pointer">
                      <input type="checkbox" v-model="mostrarLeituraColorida" id="colorido" class="tw-sr-only tw-peer" />
                      <div class="tw-w-11 tw-h-6 tw-bg-gray-700 tw-rounded-full peer peer-checked:after:tw-translate-x-full after:tw-content-[''] after:tw-absolute after:tw-top-0.5 after:tw-left-[2px] after:tw-bg-white after:tw-rounded-full after:tw-h-5 after:tw-w-5 after peer-checked:tw-bg-green-600 tw-relative"></div>
                      <span class="tw-text-xs tw-font-bold tw-opacity-70"> Exibir cores no relatório </span>
                  </label>
                </div>
                <div class="tw-flex tw-flex-col md:tw-flex-row tw-gap-3 md:tw-ml-auto">
                  <button
                    @click="gerarRelatorio"
                    :disabled="loadingDados"
                    class="tw-w-full md:tw-w-auto tw-py-3 tw-px-6 tw-rounded-xl tw-bg-green-600 tw-text-white tw-text-sm tw-font-bold hover:tw-bg-green-700 tw-shadow-lg tw-shadow-green-600/20 tw-border-none disabled:tw-opacity-50"
                  >
                    Visualizar Termometria
                  </button>
                  <button
                    @click="gerarRelatorioPdf"
                    :disabled="gerandoPdf"
                    class="tw-w-full md:tw-w-auto tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-py-3 tw-px-6 tw-rounded-xl tw-bg-blue-600 tw-text-white tw-text-sm tw-font-bold hover:tw-bg-blue-700 tw-shadow-lg tw-shadow-blue-600/20 tw-border-none disabled:tw-opacity-50"
                  >
                    <i :class="gerandoPdf ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-file-pdf'"></i>
                    {{ gerandoPdf ? 'Gerando PDF...' : 'Gerar Relatório PDF' }}
                  </button>
                  <button
                    @click="abrirModalExcluir"
                    class="tw-w-full md:tw-w-auto tw-py-3 tw-px-6 tw-rounded-xl tw-bg-red-600 tw-text-white tw-text-sm tw-font-bold hover:tw-bg-red-700 tw-shadow-lg tw-shadow-red-600/20 tw-border-none"
                  >
                    Selecionar Leituras Para Excluir
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

    <div v-if="loadingDados" class="tw-py-20 tw-flex tw-flex-col tw-items-center tw-justify-center tw-opacity-60">
        <svg class="tw-animate-spin tw-h-10 tw-w-10 tw-text-green-500" fill="none" viewBox="0 0 24 24"><circle class="tw-opacity-20" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="tw-opacity-100" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
        <span class="tw-mt-4 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-opacity-50">Sincronizando Sensores...</span>
    </div>

    <template v-else>
      <div
        v-for="rel in relatorios"
        :key="rel.silo_id"
        :class="theme === 'dark' ? 'tw-bg-[#1a1d21] tw-border-gray-800' : 'tw-bg-white tw-border-gray-100'"
        class="tw-rounded-2xl tw-shadow-2xl tw-border tw-overflow-hidden tw-relative"
      >
        <div v-if="!rel.encontrada || !rel.leitura || !rel.leitura.dados" class="tw-p-6 md:tw-p-8">
          <h3 class="tw-text-3xl tw-font-black tw-uppercase tw-tracking-tight tw-m-0">{{ rel.nome }}</h3>
          <div class="tw-mt-6 tw-flex tw-items-center tw-gap-3 tw-text-yellow-600">
            <i class="fa-solid fa-triangle-exclamation"></i>
            <span class="tw-text-base tw-font-bold">
              Leitura não encontrada na data e hora selecionada
            </span>
          </div>
        </div>

        <div v-else>
          <div class="tw-p-6 md:tw-p-8 tw-border-b tw-border-gray-800/10 tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-gap-6">
            <div>
              <h3 class="tw-text-3xl tw-font-black tw-uppercase tw-tracking-tight tw-m-0">{{ rel.nome }}</h3>
              <p class="tw-text-base tw-font-bold tw-opacity-70 tw-mt-1.5">{{ rel.leitura.data }} • {{ rel.leitura.produto }}</p>
            </div>
            
            <div class="tw-grid tw-grid-cols-2 tw-gap-4">
              <div class="tw-bg-black/5 tw-px-6 tw-py-3 tw-rounded-xl tw-text-center">
                <span class="tw-block tw-text-[10px] tw-font-black tw-opacity-40 tw-uppercase">Média Massa</span>
                <span class="tw-text-xl tw-font-black tw-text-green-500">{{ rel.leitura?.maxmedmin?.med }}°C</span>
              </div>
              <div class="tw-bg-black/5 tw-px-6 tw-py-3 tw-rounded-xl tw-text-center">
                <span class="tw-block tw-text-[10px] tw-font-black tw-opacity-40 tw-uppercase">Nível</span>
                <span class="tw-text-xl tw-font-black tw-text-blue-500">
                  {{ rel.nivel != null ? rel.nivel : '--' }}%
                </span>
              </div>
            </div>
          </div>

          <div v-if="rel.leitura.estacao" class="tw-px-8 tw-py-4 tw-border-b tw-border-gray-800/5 tw-flex tw-flex-wrap tw-gap-10 tw-bg-black/[0.02]">
              <div class="tw-flex tw-items-center tw-gap-3">
                <div class="tw-w-9 tw-h-9 tw-flex tw-items-center tw-justify-center tw-bg-orange-500/10 tw-rounded-xl">
                    <i class="fa-solid fa-temperature-high tw-text-orange-500 tw-text-sm"></i>
                </div>
                <div>
                    <span class="tw-block tw-text-[10px] tw-font-black tw-opacity-40 tw-uppercase">Ambiente</span>
                    <span class="tw-text-sm tw-font-black">{{ rel.leitura.estacao.temperatura }}°C</span>
                </div>
             </div>
             <div class="tw-flex tw-items-center tw-gap-3">
                <div class="tw-w-9 tw-h-9 tw-flex tw-items-center tw-justify-center tw-bg-blue-500/10 tw-rounded-xl">
                    <i class="fa-solid fa-droplet tw-text-blue-500 tw-text-sm"></i>
                </div>
                <div>
                    <span class="tw-block tw-text-[10px] tw-font-black tw-opacity-40 tw-uppercase">Umidade Rel.</span>
                    <span class="tw-text-sm tw-font-black">{{ rel.leitura.estacao.umidade }}%</span>
                </div>
              </div>
              <div class="tw-flex tw-items-center tw-gap-3">
                <div :class="rel.leitura.estacao.chuva ? 'tw-bg-cyan-500/10' : 'tw-bg-yellow-500/10'" class="tw-w-9 tw-h-9 tw-flex tw-items-center tw-justify-center tw-rounded-xl">
                    <i :class="rel.leitura.estacao.chuva ? 'fa-solid fa-cloud-showers-heavy tw-text-cyan-500' : 'fa-solid fa-sun tw-text-yellow-500'" class="tw-text-sm"></i>
                </div>
                <div>
                    <span class="tw-block tw-text-[10px] tw-font-black tw-opacity-40 tw-uppercase">Clima</span>
                    <span class="tw-text-sm tw-font-black">{{ rel.leitura.estacao.chuva ? 'CHOVENDO' : 'SEM CHUVA' }}</span>
                </div>
             </div>
          </div>

          <div class="tw-overflow-x-auto">
            <table class="tw-w-full tw-text-center tw-border-none">
              <thead>
                <tr :class="theme === 'dark' ? 'tw-bg-black/40' : 'tw-bg-gray-100/80'">
                  <th class="tw-px-4 tw-py-4 tw-text-[11px] tw-font-black tw-uppercase tw-tracking-widest tw-border-r tw-border-gray-800/10 tw-w-20 tw-sticky tw-bg-inherit">CABO</th>
                  <th v-for="i in 16" :key="i" class="tw-px-1 tw-py-4 tw-text-[11px] tw-font-black tw-opacity-40">S{{i}}</th>
                </tr>
              </thead>
              <tbody class="tw-divide-y tw-divide-gray-800/5">
                <tr v-for="cabo in rel.leitura.dados" :key="cabo.cabo" class="hover:tw-bg-black/5">
                  <td class="tw-px-4 tw-py-4 tw-text-xs tw-font-black tw-bg-black/10 tw-border-r tw-border-gray-800/10 tw-sticky">{{ cabo.cabo }}</td>
                  <td 
                    v-for="index in 16" :key="index"
                    :style="mostrarLeituraColorida && isValido(cabo.temperaturas[`s${index}`])
                      ? { backgroundColor: getCorPorTemperatura(cabo.temperaturas[`s${index}`]) }
                      : (isOff(cabo.temperaturas[`s${index}`]) ? { backgroundColor: '#ffffff' } : {})"
                    :class="[
                       'tw-px-1 tw-py-4 tw-text-base tw-font-mono tw-font-black',
                       isValido(cabo.temperaturas[`s${index}`])
                         ? (theme === 'dark' ? 'tw-text-gray-100' : 'tw-text-gray-900')
                         : (isOff(cabo.temperaturas[`s${index}`]) ? 'tw-text-black' : 'tw-opacity-0')
                    ]"
                  >
                    {{ isValido(cabo.temperaturas[`s${index}`])
                       ? parseFloat(cabo.temperaturas[`s${index}`]).toFixed(1)
                       : (isOff(cabo.temperaturas[`s${index}`]) ? 'OFF' : '') }}
                  </td>
                </tr>
              </tbody>
            </table>
            </div>
          </div>
        </div>

        <div v-if="relatorios.length === 0" class="tw-py-20 tw-text-center tw-opacity-30">
            <p class="tw-text-xs tw-font-black tw-uppercase tw-tracking-widest">Selecione os silos e o período para visualizar a matriz</p>
        </div>
      </template>
      </div>
    </div>

    <!-- Modal: Excluir Leituras -->
    <div v-if="modalExcluirAberto" class="tw-fixed tw-inset-0 tw-z-[1200] tw-flex tw-items-center tw-justify-center tw-p-4" :data-theme="theme">
      <div class="tw-absolute tw-inset-0 tw-bg-black/50" @click="fecharModalExcluir"></div>
      <div
        :class="theme === 'dark' ? 'tw-bg-[#1a1d21] tw-text-gray-100' : 'tw-bg-white tw-text-gray-800'"
        class="tw-relative tw-z-10 tw-w-full tw-max-w-lg tw-rounded-2xl tw-shadow-2xl tw-overflow-hidden tw-flex tw-flex-col tw-max-h-[85vh]"
      >
        <div class="tw-flex tw-items-center tw-justify-between tw-p-5 tw-border-b tw-border-gray-800/10">
          <div class="tw-flex tw-items-center tw-gap-3">
            <div class="tw-p-2 tw-rounded-xl tw-bg-red-500/10"><i class="fa-solid fa-trash-can tw-text-red-500"></i></div>
            <h3 class="tw-text-base tw-font-bold tw-m-0">Excluir Leituras</h3>
          </div>
          <button @click="fecharModalExcluir" class="tw-p-2 tw-rounded-lg hover:tw-bg-black/5 tw-border-none tw-bg-transparent tw-cursor-pointer">
            <i class="fa-solid fa-xmark tw-opacity-60"></i>
          </button>
        </div>

        <div class="tw-px-5 tw-py-3 tw-border-b tw-border-gray-800/10 tw-flex tw-items-center tw-justify-between">
          <label class="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
            <input type="checkbox" :checked="todasSelecionadasExcluir" @change="toggleTodasExcluir" class="tw-accent-red-600" />
            <span class="tw-text-xs tw-font-bold">Selecionar todas</span>
          </label>
          <span class="tw-text-xs tw-opacity-50">{{ selectedParaExcluir.length }} selecionada(s)</span>
        </div>

        <div class="tw-flex-1 tw-overflow-y-auto tw-p-3 base-modal-scroll">
          <div v-for="grupo in leiturasAgrupadasPorSilo" :key="grupo.silo_id" class="tw-mb-3">
            <div class="tw-px-2 tw-py-1 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-opacity-50">{{ grupo.silo_nome }}</div>
            <label
              v-for="item in grupo.itens" :key="item._key"
              class="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2 tw-rounded-lg hover:tw-bg-black/5 tw-cursor-pointer"
            >
              <input type="checkbox" :value="item._id" v-model="selectedParaExcluir" class="tw-accent-red-600" />
              <span class="tw-text-xs">{{ item.data }}</span>
            </label>
          </div>
        </div>

        <div class="tw-p-4 tw-border-t tw-border-gray-800/10 tw-flex tw-justify-end tw-gap-3">
          <button @click="fecharModalExcluir" class="tw-px-4 tw-py-2 tw-rounded-xl tw-text-xs tw-font-bold tw-opacity-70 hover:tw-opacity-100 tw-border-none tw-bg-transparent tw-cursor-pointer">
            Cancelar
          </button>
          <button
            @click="confirmarExclusao"
            :disabled="excluindo || selectedParaExcluir.length === 0"
            class="tw-px-5 tw-py-2 tw-rounded-xl tw-bg-red-600 tw-text-white tw-text-xs tw-font-bold hover:tw-bg-red-700 tw-border-none tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            {{ excluindo ? 'Excluindo...' : `Excluir ${selectedParaExcluir.length} leitura(s)` }}
          </button>
        </div>
      </div>
    </div>
  </MainLayout>
</template>

<script>
/* eslint-disable */
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import MainLayout from '@/components/layouts/v2/MainLayout.vue';
import { useToast } from "vue-toastification";
import LoadingRelatorio from "@/components/ui/App/LoadingRelatorio.vue";
import BaseSelect from "@/components/ui/v2/BaseSelect.vue";
import temperaturaCores from "@/assets/colors.json";

const JANELA_MINUTOS = 30;

export default {
    name: 'RelatorioTermometria',
    components: { MainLayout, LoadingRelatorio, BaseSelect },

    setup() {
        const store = useStore();
        const toast = useToast();
        const today = new Date().toISOString().split("T")[0];
        const dataInicio = ref(today);
        const dataFinal = ref(today);
        const loadingDados = ref(false);
        const buscandoLeituras = ref(false);
        const selectedSilos = ref([]);
        const siloDropdownOpen = ref(false);
        const leiturasEncontradas = ref([]);
        const selectedLeitura = ref(null);
        const relatorios = ref([]);
        const mostrarLeituraColorida = ref(true);
        const modalExcluirAberto = ref(false);
        const selectedParaExcluir = ref([]);
        const excluindo = ref(false);
        const gerandoPdf = ref(false);

        const theme = computed(() => store.state.ui.theme);
        const unidade = computed(() => store.state.unidade.unidade || {});
        const silosFiltered = computed(() => {
          if (!unidade.value?.id) return store.state.silos.silos || [];
          return (store.state.silos.silos || []).filter(s => String(s.unidade_id) === String(unidade.value.id));
        });

        const todosSilosSelecionados = computed(() =>
            silosFiltered.value.length > 0 && selectedSilos.value.length === silosFiltered.value.length
        );

        const siloSelectionLabel = computed(() => {
            if (selectedSilos.value.length === 0) return 'Selecione os silos...';
            if (selectedSilos.value.length === 1) {
                const s = silosFiltered.value.find(s => s.id === selectedSilos.value[0]);
                return s?.nome || '1 silo selecionado';
            }
            return `${selectedSilos.value.length} silos selecionados`;
        });

        const toggleTodosSilos = () => {
            if (todosSilosSelecionados.value) {
                selectedSilos.value = [];
            } else {
                selectedSilos.value = silosFiltered.value.map(s => s.id);
            }
        };

        watch(selectedSilos, () => {
            leiturasEncontradas.value = [];
            selectedLeitura.value = null;
            relatorios.value = [];
        }, { deep: true });

        const isValido = (temp) => {
            if (temp === undefined || temp === null || isNaN(temp) || Number(temp) > 99) return false;
            return true;
        };

        const isOff = (temp) => {
            if (temp === undefined || temp === null || temp === '' || isNaN(Number(temp))) return false;
            return Number(temp) > 99;
        };

        const horaLabel = (leitura) => (leitura?.data?.split(' ')[1] || '').slice(0, 5);

        const leiturasAgrupadas = computed(() => {
            const grupos = new Map();
            const vistos = new Set();
            for (const leitura of leiturasEncontradas.value) {
                const dataParte = (leitura.data || '').split(' ')[0];
                const hhmm = horaLabel(leitura);
                const chave = `${dataParte}|${hhmm}`;
                if (vistos.has(chave)) continue;
                vistos.add(chave);
                if (!grupos.has(dataParte)) grupos.set(dataParte, { data: dataParte, itens: [] });
                grupos.get(dataParte).itens.push(leitura);
            }
            return Array.from(grupos.values());
        });

        const leiturasAgrupadasPorSilo = computed(() => {
            const grupos = new Map();
            for (const leitura of leiturasEncontradas.value) {
                if (!grupos.has(leitura.silo_id)) {
                    grupos.set(leitura.silo_id, { silo_id: leitura.silo_id, silo_nome: leitura.silo_nome, itens: [] });
                }
                grupos.get(leitura.silo_id).itens.push(leitura);
            }
            return Array.from(grupos.values());
        });

        const todasSelecionadasExcluir = computed(() =>
            leiturasEncontradas.value.length > 0 && selectedParaExcluir.value.length === leiturasEncontradas.value.length
        );

        const toggleTodasExcluir = () => {
            if (todasSelecionadasExcluir.value) {
                selectedParaExcluir.value = [];
            } else {
                selectedParaExcluir.value = leiturasEncontradas.value.map(l => l._id);
            }
        };

        const abrirModalExcluir = () => {
            selectedParaExcluir.value = [];
            modalExcluirAberto.value = true;
        };

        const fecharModalExcluir = () => {
            if (excluindo.value) return;
            modalExcluirAberto.value = false;
        };

        const confirmarExclusao = async () => {
            if (selectedParaExcluir.value.length === 0) return toast.warning('Selecione ao menos uma leitura.');
            if (!unidade.value?.id) return toast.warning('Unidade não carregada.');
            try {
                excluindo.value = true;
                const idsExcluidos = [...selectedParaExcluir.value];
                await store.dispatch('leituras/excluirLeiturasTermometriaEmLote', {
                    unidadeId: unidade.value.id,
                    leitura_ids: idsExcluidos
                });

                const excluidos = new Set(idsExcluidos.map(Number));
                leiturasEncontradas.value = leiturasEncontradas.value.filter(l => !excluidos.has(Number(l._id)));
                if (selectedLeitura.value && excluidos.has(Number(selectedLeitura.value._id))) {
                    selectedLeitura.value = leiturasEncontradas.value[0] || null;
                }
                relatorios.value = relatorios.value.filter(r => !(r.leitura && excluidos.has(Number(r.leitura._id))));

                selectedParaExcluir.value = [];
                modalExcluirAberto.value = false;
                toast.success('Leitura(s) excluída(s) com sucesso.');
            } catch (e) {
                console.error('Erro ao excluir leituras:', e);
                toast.error('Erro ao excluir leituras');
            } finally { excluindo.value = false; }
        };

        const fetchLeituras = async () => {
            if (selectedSilos.value.length === 0) return toast.warning('Selecione ao menos um silo.');
            if (!unidade.value?.id) return toast.warning('Unidade não carregada.');
            try {
                buscandoLeituras.value = true;
                siloDropdownOpen.value = false;
                leiturasEncontradas.value = [];
                selectedLeitura.value = null;
                relatorios.value = [];

                const data = await store.dispatch("leituras/fetchLeiturasTermometriaEmLote", {
                    unidadeId: unidade.value.id,
                    silo_ids: selectedSilos.value,
                    data_inicio: dataInicio.value,
                    data_final: dataFinal.value
                });

                const combinadas = (data?.leituras || []).map(l => ({
                    ...l,
                    _key: `${l.silo_id}-${l._id}`
                }));
                leiturasEncontradas.value = combinadas;

                if (combinadas.length > 0) {
                    selectedLeitura.value = combinadas[0];
                } else {
                    toast.info('Nenhuma leitura encontrada.');
                }
            } catch (e) { toast.error('Erro ao buscar lista'); }
            finally { buscandoLeituras.value = false; }
        };

        const gerarRelatorio = async () => {
            if (!selectedLeitura.value?._id) return;
            if (!unidade.value?.id) return toast.warning('Unidade não carregada.');
            try {
                loadingDados.value = true;
                relatorios.value = [];

                const data = await store.dispatch("leituras/fetchRelatorioTermometriaEmLote", {
                    unidadeId: unidade.value.id,
                    silo_ids: selectedSilos.value,
                    leitura_referencia_id: selectedLeitura.value._id,
                    janela_minutos: JANELA_MINUTOS
                });

                relatorios.value = data?.silos || [];
              } catch (error) { toast.error('Erro ao carregar matriz'); }
            finally { setTimeout(() => { loadingDados.value = false; }, 300); }
        };

        const gerarRelatorioPdf = async () => {
            if (selectedSilos.value.length === 0) return toast.warning('Selecione ao menos um silo.');
            if (!selectedLeitura.value?._id) return toast.warning('Selecione uma leitura.');
            if (!unidade.value?.id) return toast.warning('Unidade não carregada.');
            try {
                gerandoPdf.value = true;
                const response = await store.dispatch("leituras/downloadRelatorioTermometriaLotePdf", {
                    unidadeId: unidade.value.id,
                    silo_ids: [...selectedSilos.value],
                    leitura_referencia_id: selectedLeitura.value._id,
                    janela_minutos: JANELA_MINUTOS,
                    habilita_cor: mostrarLeituraColorida.value
                });

                let filename = 'relatorio_termometria.pdf';
                const disposition = response.headers['content-disposition'];
                if (disposition) {
                    const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
                    if (match && match[1]) filename = decodeURIComponent(match[1]);
                }

                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Erro ao gerar PDF de termometria:', error);
                toast.error('Erro ao gerar o PDF do relatório');
            } finally {
                gerandoPdf.value = false;
            }
        };

        const getCorPorTemperatura = (temp) => {
            if (!isValido(temp)) return 'transparent';
            const val = Number(temp);
            const faixas = Object.keys(temperaturaCores).map(Number).sort((a, b) => a - b);            
            for (let i = 0; i < faixas.length; i++) {
                // Adicionamos 'CC' no final do HEX para dar 80% de transparência e suavizar o fundo
                if (val <= faixas[i]) return `${temperaturaCores[faixas[i]]}CC`;
            }
            return "#c31313CC";
        };

        return {
            theme, unidade, silosFiltered, dataInicio, dataFinal,
            selectedSilos, siloDropdownOpen, siloSelectionLabel, todosSilosSelecionados, toggleTodosSilos,
            loadingDados, gerarRelatorio,
            gerarRelatorioPdf, gerandoPdf,
            leiturasEncontradas, leiturasAgrupadas, horaLabel, selectedLeitura, fetchLeituras,
            relatorios, mostrarLeituraColorida,
            buscandoLeituras, getCorPorTemperatura, isValido, isOff,
            modalExcluirAberto, selectedParaExcluir, excluindo,
            leiturasAgrupadasPorSilo, todasSelecionadasExcluir, toggleTodasExcluir,
            abrirModalExcluir, fecharModalExcluir, confirmarExclusao
        };
    },
}
</script>

<style scoped>
.tw-custom-input { @apply tw-w-full tw-border-none tw-rounded-xl tw-p-3 md:tw-p-4 tw-text-sm tw-outline-none; }
[data-theme="dark"] .tw-custom-input { background-color: rgba(39, 43, 47, 0.45); color: #f3f4f6; }
[data-theme="light"] .tw-custom-input { background-color: #f3f4f6; color: #1f2937; }
.tw-custom-input:focus { @apply tw-ring-2 tw-ring-green-500/20; }

::-webkit-scrollbar { width: 4px; height: 4px; }
::-webkit-scrollbar-thumb { background: #33b35a; border-radius: 10px; }
</style>